import React, { useState, useEffect, useRef } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import Input from '@/component/input/Input.tsx'
import { apiLogin, LOGIN_SERVER_UNREACHABLE, RATE_LIMITED_MESSAGE } from '@/api/auth/login.ts'
import { useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useLoginStore } from '@/store/LoginStore.ts'
import CarearlyLogo from '@/assets/ic_carearly_logo.svg?react'
import CarearlyWhiteLogo from '@/assets/ic_carearly_logo_white.svg?react'
import SolidButton from '@/component/button/SolidButton.tsx'
import CheckBox from '@/component/checkbox/CheckBox.tsx'
import { isEmpty } from '@/util/strings.ts'
import { useTranslation } from 'react-i18next'
import { TURNSTILE_SITE_KEY } from '@/constant/constant.ts'
import { APP_PRODUCT_LABEL } from '@/constant/version.ts'
import Menu from '@/component/menu/Menu.tsx'
import { useLanguageOptions } from '@/locales/useLanguageOptions.ts'
import { ChevronDownIcon, GlobeIcon } from '@/component/layout/ShellIcons.tsx'

type IProps = {}

/** 게이트웨이가 `Retry-After` 를 알려 주지 않았을 때 안내할 대기 시간 */
const DEFAULT_RATE_LIMIT_WAIT_SECONDS = 60

const formatLockTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const Login: React.FC<IProps> = ({}) => {
    const { t } = useTranslation()
    const { login, setAutoLogin, isAutoLogin, isSaveID, setSaveID, savedID, accessToken } =
        useLoginStore()

    const [id, setId] = useState(savedID as string)
    const [password, setPassword] = useState('')
    const [turnstileRequired, setTurnstileRequired] = useState(false)
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [lockUntil, setLockUntil] = useState<number | null>(null)
    const [lockRemaining, setLockRemaining] = useState(0)
    const [lockMaxAttempts, setLockMaxAttempts] = useState(0)
    const turnstileRef = useRef<HTMLDivElement>(null)
    const turnstileWidgetId = useRef<string | null>(null)
    const navigate = useNavigate()
    const language = useLanguageOptions()

    useEffect(() => {
        if (!lockUntil) return

        const tick = () => {
            const remaining = Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000))
            setLockRemaining(remaining)
            if (remaining === 0) setLockUntil(null)
        }

        tick()
        const timer = window.setInterval(tick, 1000)
        return () => window.clearInterval(timer)
    }, [lockUntil])

    // 이미 로그인된 상태라면 메인으로 리다이렉트
    useEffect(() => {
        if (accessToken) {
            navigate('/', { replace: true })
        }
    }, [accessToken, navigate])

    // Turnstile 위젯 렌더링
    useEffect(() => {
        if (!turnstileRequired || !turnstileRef.current || !TURNSTILE_SITE_KEY) return

        const renderTurnstile = () => {
            if (window.turnstile && turnstileRef.current) {
                if (turnstileWidgetId.current) {
                    window.turnstile.remove(turnstileWidgetId.current)
                }
                turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token: string) => setTurnstileToken(token),
                    'expired-callback': () => setTurnstileToken(null)
                })
            }
        }

        if (window.turnstile) {
            renderTurnstile()
        } else if (!document.querySelector('script[src*="turnstile"]')) {
            const script = document.createElement('script')
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
            script.async = true
            script.onload = renderTurnstile
            document.head.appendChild(script)
        }
    }, [turnstileRequired])

    const onClickLogin = async () => {
        if (isSubmitting) return

        setIsSubmitting(true)
        try {
            const result = await apiLogin({
                id,
                password,
                rememberMe: !!isAutoLogin,
                ...(turnstileToken && { turnstileToken })
            })

            if (result.message === 'ACCOUNT_LOCKED') {
                const seconds = result.remainingLockSeconds ?? 0
                const maxAttempts = result.maxAttempts ?? 5
                setLockUntil(Date.now() + seconds * 1000)
                setLockMaxAttempts(maxAttempts)
                toast.error(
                    t('login.submit.locked', {
                        attempts: maxAttempts,
                        time: formatLockTime(seconds)
                    })
                )
                return
            }

            if (result.message === RATE_LIMITED_MESSAGE) {
                toast.error(
                    t('login.submit.rateLimited', {
                        time: formatLockTime(result.retryAfterSeconds ?? DEFAULT_RATE_LIMIT_WAIT_SECONDS)
                    })
                )
                return
            }

            if (result.turnstileRequired || result.data?.turnstileRequired) {
                setTurnstileRequired(true)
                setTurnstileToken(null)
                toast.warning(t('login.turnstileRequired'))
                return
            }

            const accessToken = result.data?.token || result.data?.accessToken
            if (accessToken) {
                await login(id, accessToken)
                toast.success(t('login.submit.success'))
                navigate('/')
            } else {
                const failCount = result.loginFailCount
                if (failCount && failCount >= 3) {
                    setTurnstileRequired(true)
                }
                if (turnstileWidgetId.current && window.turnstile) {
                    window.turnstile.reset(turnstileWidgetId.current)
                    setTurnstileToken(null)
                }
                const remainingAttempts = result.remainingAttempts
                if (remainingAttempts !== undefined && remainingAttempts > 0) {
                    toast.error(t('login.submit.failRemaining', { remaining: remainingAttempts }))
                } else {
                    toast.error(
                        result.message === LOGIN_SERVER_UNREACHABLE
                            ? t('login.error.serverUnreachable')
                            : result.message || t('login.submit.fail')
                    )
                }
            }
        } catch {
            toast.error(t('login.submit.fail'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <PageConfig className={'login-page'}>
            <main className={'login-shell'}>
                <section className={'login-brand-panel'}>
                    <Link to={'/'} className={'login-brand-panel__logo'} aria-label={t('app.title.carearly')}>
                        <CarearlyWhiteLogo />
                    </Link>
                    <div className={'login-brand-panel__copy'}>
                        <p>CAREARLY SENIOR CARE OPERATIONS</p>
                        <h1>{t('home.introduction.title')}</h1>
                        <span>{t('home.introduction.desc')}</span>
                    </div>
                    <div className={'login-brand-panel__features'}>
                        {[
                            t('home.feature.options.bio.title'),
                            t('home.feature.options.emergency.title'),
                            t('home.feature.options.location.title')
                        ].map((item, index) => (
                            <div key={item}>
                                <strong>0{index + 1}</strong>
                                <span>{item}</span>
                            </div>
                        ))}
                    </div>
                    <small>24/7 CONNECTED SENIOR CARE</small>
                </section>
                <section className={'login-form-panel'}>
                    <Menu
                        className={'login-language'}
                        items={language.languages.map((option) => ({
                            id: option.id,
                            text: option.label,
                            onClick: () => language.changeLanguage(option.id)
                        }))}
                        ariaLabel={t('topNav.languageSelect')}
                        render={
                            <span className={'login-language__trigger'}>
                                <GlobeIcon className={'login-language__icon'} />
                                <span>{language.current.label}</span>
                                <ChevronDownIcon className={'login-language__chevron'} />
                            </span>
                        }
                    />
                    <div className={'login-form-card'}>
                        <Link to={'/'} className={'login-form-card__mobile-logo'} aria-label={t('app.title.carearly')}>
                            <CarearlyLogo />
                        </Link>
                        <div className={'login-form-card__heading'}>
                            <p>WELCOME BACK</p>
                            <h2>{t('login.confirm')}</h2>
                            <span>{t('login.customerCenter.desc')}</span>
                        </div>
                        <Input
                            className={'login-form-card__field'}
                            inputClassName={'w-full'}
                            value={id}
                            onChanged={(text) => setId(text)}
                            inputType={'text'}
                            autoComplete={'username'}
                            placeholder={t('login.id.placeholder')}
                        />
                        <Input
                            className={'login-form-card__field'}
                            inputClassName={'w-full'}
                            value={password}
                            inputType={'password'}
                            autoComplete={'current-password'}
                            placeholder={t('login.password.placeholder')}
                            onPressEnter={onClickLogin}
                            onChanged={(text) => setPassword(text)}
                        />

                        {turnstileRequired && <div ref={turnstileRef} className={'mt-[10px]'} />}

                        {lockUntil && lockRemaining > 0 && (
                            <p className={'login-form-card__lock-notice'} role="alert">
                                {t('login.locked.remaining', {
                                    attempts: lockMaxAttempts,
                                    time: formatLockTime(lockRemaining)
                                })}
                            </p>
                        )}

                        <SolidButton
                            size={'medium'}
                            text={t('login.confirm')}
                            onClick={onClickLogin}
                            disabled={
                                isEmpty(id) ||
                                isEmpty(password) ||
                                isSubmitting ||
                                (lockUntil !== null && lockRemaining > 0) ||
                                (turnstileRequired && !turnstileToken)
                            }
                            isLoading={isSubmitting}
                            className={'login-form-card__submit w-full'}
                        />
                        <div className={'login-form-card__options'}>
                            <CheckBox
                                id={'save_id'}
                                label={t('login.saveId')}
                                onChanged={(isChecked) => setSaveID(isChecked)}
                                value={isSaveID}
                            />
                            <CheckBox
                                id={'auto_login'}
                                label={t('login.autoLogin')}
                                onChanged={(isChecked) => setAutoLogin(isChecked)}
                                value={isAutoLogin}
                            />
                        </div>
                        <div className={'login-form-card__support'}>
                            <span>{t('login.customerCenter.desc')} · {APP_PRODUCT_LABEL}</span>
                            <a href={`tel:${t('login.customerCenter.desc.phone')}`}>
                                {t('login.customerCenter.desc.phone')}
                            </a>
                        </div>
                    </div>
                </section>
            </main>
        </PageConfig>
    )
}

export default Login
